import { useState, useEffect } from 'react'
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  AlertCircle,
  CheckCircle,
  ArrowRight,
  X,
  Shield,
} from 'lucide-react'
import { apiGet, apiPost, apiPut, apiDelete } from '../../services/api'

interface RoleMapping {
  id: number
  company_id: number | null
  ev_role: string | null
  ev_designation_id: number | null
  ev_designation_name: string | null
  aeon_role: string
  description: string | null
  is_active: boolean
  priority: number
}

interface RoleOption {
  value: string
  label: string
}

interface EvDesignation {
  id: number
  name: string
}

interface RoleMappingsModalProps {
  isOpen: boolean
  onClose: () => void
  companyId: number
  companyName: string
}

export default function RoleMappingsModal({ isOpen, onClose, companyId, companyName }: RoleMappingsModalProps) {
  const [mappings, setMappings] = useState<RoleMapping[]>([])
  const [aeonRoles, setAeonRoles] = useState<RoleOption[]>([])
  const [evRoles, setEvRoles] = useState<RoleOption[]>([])
  const [evDesignations, setEvDesignations] = useState<EvDesignation[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // Form state
  const [showForm, setShowForm] = useState(false)
  const [editingItem, setEditingItem] = useState<RoleMapping | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    ev_role: '',
    ev_designation_id: '',
    ev_designation_name: '',
    aeon_role: '',
    description: '',
    is_active: true,
    priority: 0,
  })

  useEffect(() => {
    if (isOpen) {
      fetchData()
    }
  }, [isOpen, companyId])

  const fetchData = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const [mappingsRes, evOptionsRes] = await Promise.all([
        apiGet<{
          data: RoleMapping[]
          aeon_roles: RoleOption[]
          ev_roles: RoleOption[]
        }>(`/role-mappings?company_id=${companyId}`),
        apiGet<{
          ev_roles: RoleOption[]
          aeon_roles: RoleOption[]
          ev_designations: EvDesignation[]
        }>('/role-mappings/ev-options')
      ])

      // Filter mappings for this company or global
      const companyMappings = mappingsRes.data.filter(
        m => m.company_id === companyId || m.company_id === null
      )
      setMappings(companyMappings)
      // Prefer ev-options response (fetched from EV API) for both roles
      setEvRoles(evOptionsRes.ev_roles || mappingsRes.ev_roles)
      setAeonRoles(evOptionsRes.aeon_roles || mappingsRes.aeon_roles)
      setEvDesignations(evOptionsRes.ev_designations || [])
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load role mappings')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCreate = () => {
    setEditingItem(null)
    setFormData({
      ev_role: '',
      ev_designation_id: '',
      ev_designation_name: '',
      aeon_role: aeonRoles.length > 0 ? aeonRoles[0].value : '',
      description: '',
      is_active: true,
      priority: 0,
    })
    setShowForm(true)
  }

  const handleEdit = (mapping: RoleMapping) => {
    setEditingItem(mapping)
    setFormData({
      ev_role: mapping.ev_role || '',
      ev_designation_id: mapping.ev_designation_id?.toString() || '',
      ev_designation_name: mapping.ev_designation_name || '',
      aeon_role: mapping.aeon_role,
      description: mapping.description || '',
      is_active: mapping.is_active,
      priority: mapping.priority,
    })
    setShowForm(true)
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Delete this role mapping?')) return

    try {
      await apiDelete(`/role-mappings/${id}`)
      setSuccess('Deleted')
      fetchData()
      setTimeout(() => setSuccess(null), 2000)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete')
    }
  }

  const handleSubmit = async () => {
    setIsSubmitting(true)
    setError(null)

    try {
      const payload = {
        company_id: companyId,
        ev_role: formData.ev_role || null,
        ev_designation_id: formData.ev_designation_id ? parseInt(formData.ev_designation_id) : null,
        ev_designation_name: formData.ev_designation_name || null,
        aeon_role: formData.aeon_role,
        description: formData.description || null,
        is_active: formData.is_active,
        priority: formData.priority,
      }

      if (editingItem) {
        await apiPut(`/role-mappings/${editingItem.id}`, payload)
        setSuccess('Updated')
      } else {
        await apiPost('/role-mappings', payload)
        setSuccess('Created')
      }

      setShowForm(false)
      fetchData()
      setTimeout(() => setSuccess(null), 2000)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save')
    } finally {
      setIsSubmitting(false)
    }
  }

  const getEvRoleLabel = (role: string | null) => {
    if (!role) return 'Any'
    return evRoles.find(r => r.value === role)?.label || role
  }

  const getAeonRoleLabel = (role: string) => {
    return aeonRoles.find(r => r.value === role)?.label || role
  }

  const getAeonRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'superadmin': return 'bg-purple-100 text-purple-800'
      case 'company_admin': return 'bg-blue-100 text-blue-800'
      case 'supervisor': return 'bg-green-100 text-green-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between bg-gray-50">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-blue-600" />
            <div>
              <h2 className="text-lg font-semibold">Role Mappings</h2>
              <p className="text-sm text-gray-500">{companyName}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Messages */}
          {error && (
            <div className="mb-4 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <AlertCircle className="h-4 w-4" />
              <span>{error}</span>
              <button onClick={() => setError(null)} className="ml-auto">
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
          {success && (
            <div className="mb-4 flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
              <CheckCircle className="h-4 w-4" />
              <span>{success}</span>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
            </div>
          ) : showForm ? (
            /* Form */
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">EV Role</label>
                  <select
                    className="input"
                    value={formData.ev_role}
                    onChange={(e) => setFormData({ ...formData, ev_role: e.target.value })}
                  >
                    <option value="">Any Role</option>
                    {evRoles.map((role) => (
                      <option key={role.value} value={role.value}>{role.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">EV Designation</label>
                  {evDesignations.length > 0 ? (
                    <select
                      className="input"
                      value={formData.ev_designation_id}
                      onChange={(e) => {
                        const desigId = e.target.value
                        const desig = evDesignations.find(d => d.id.toString() === desigId)
                        setFormData({
                          ...formData,
                          ev_designation_id: desigId,
                          ev_designation_name: desig?.name || ''
                        })
                      }}
                    >
                      <option value="">Any Designation</option>
                      {evDesignations.map((d) => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      className="input"
                      placeholder="Designation name"
                      value={formData.ev_designation_name}
                      onChange={(e) => setFormData({ ...formData, ev_designation_name: e.target.value })}
                    />
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">AEON KPI Role *</label>
                  <select
                    className="input"
                    value={formData.aeon_role}
                    onChange={(e) => setFormData({ ...formData, aeon_role: e.target.value })}
                  >
                    {aeonRoles.map((role) => (
                      <option key={role.value} value={role.value}>{role.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <input
                    type="number"
                    className="input"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  className="input"
                  placeholder="Optional description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>

              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm">Active</span>
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="btn-primary flex items-center gap-2"
                >
                  {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  {editingItem ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          ) : (
            /* List */
            <>
              <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-gray-500">
                  {mappings.length} mapping{mappings.length !== 1 ? 's' : ''}
                </p>
                <button onClick={handleCreate} className="btn-primary text-sm flex items-center gap-1">
                  <Plus className="h-4 w-4" />
                  Add Mapping
                </button>
              </div>

              {mappings.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Shield className="h-12 w-12 mx-auto mb-2 opacity-30" />
                  <p>No role mappings configured</p>
                  <p className="text-sm">Default mapping will be used</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {mappings.map((mapping) => (
                    <div
                      key={mapping.id}
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        mapping.company_id === null ? 'bg-gray-50 border-dashed' : 'bg-white'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="text-sm">
                          <span className="font-medium">{getEvRoleLabel(mapping.ev_role)}</span>
                          {mapping.ev_designation_name && (
                            <span className="text-gray-500"> + {mapping.ev_designation_name}</span>
                          )}
                        </div>
                        <ArrowRight className="h-4 w-4 text-gray-400" />
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getAeonRoleBadgeColor(mapping.aeon_role)}`}>
                          {getAeonRoleLabel(mapping.aeon_role)}
                        </span>
                        {mapping.company_id === null && (
                          <span className="text-xs text-gray-400">(Global)</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        {mapping.company_id === companyId && (
                          <>
                            <button
                              onClick={() => handleEdit(mapping)}
                              className="p-1 text-gray-400 hover:text-blue-600"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(mapping.id)}
                              className="p-1 text-gray-400 hover:text-red-600"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50">
          <p className="text-xs text-gray-500">
            Higher priority mappings are checked first. Global mappings apply to all companies.
          </p>
        </div>
      </div>
    </div>
  )
}
